import React, { useState, useEffect } from "react";
import Header from "../components/Header";
import { useNavigate } from "react-router-dom";
import { orderService, stockService } from "../services/apiService";
import Toast from "../components/Toast";
import { useBill } from "../hooks/useBill";
import { useLoader } from "../utils/LoaderContext";

// Shared input class for consistent textfield color across all editable cells
const INPUT_CLS =
  "w-full text-xs border border-blue-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white text-gray-900 placeholder-gray-400";

const DRP_CLS =
  "w-full text-center font-semibold text-blue-800 border border-blue-200 rounded-lg py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white placeholder-gray-400";
const DiscountCombobox = ({
  value,
  maxDiscount,
  disabled,
  onChange,
  onError,
}) => {
  const [input, setInput] = useState(value ? String(value) : "");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setInput(value ? String(value) : "");
  }, [value]);

  const options = (() => {
    const intOpts = Array.from({ length: Math.floor(Math.max(5, maxDiscount)) + 1 }, (_, i) => i);
    if (maxDiscount % 1 !== 0) intOpts.push(parseFloat(maxDiscount.toFixed(2)));
    return intOpts.filter((i) => i <= maxDiscount && (String(i).startsWith(input.trim()) || input.trim() === ""));
  })();

  // Color logic: max = red, average (middle) = green, others = default
  const getDiscColor = (val) => {
    if (maxDiscount <= 0) return "text-gray-700";
    if (val === maxDiscount) return "text-red-600 font-bold";
    const avg = Math.round(maxDiscount / 2);
    if (val === avg) return "text-green-600 font-bold";
    if (val > avg) return "text-orange-600";
    return "text-gray-700";
  };

  // Input field color based on current value
  const inputColor = (() => {
    const v = parseFloat(input) || 0;
    if (maxDiscount <= 0 || disabled) return "";
    if (v === maxDiscount) return "text-red-600 font-bold";
    const avg = Math.round(maxDiscount / 2);
    if (v >= avg) return "text-green-600 font-semibold";
    return "";
  })();

  const select = (val) => {
    setInput(String(val));
    setOpen(false);
    onChange(val);
  };

  const handleBlur = () => {
    setTimeout(() => {
      setOpen(false);
      const parsed = Math.min(maxDiscount, Math.max(0, parseFloat(input) || 0));
      if (parseFloat(input) > maxDiscount) {
        onError && onError(maxDiscount);
        setInput(String(value ?? 0));
      } else {
        onChange(parsed);
        setInput(String(parsed));
      }
    }, 150);
  };

  return (
    <div className="relative w-full">
      <input
        type="text"
        value={input}
        disabled={disabled}
        onChange={(e) => {
          setInput(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={handleBlur}
        placeholder="0"
        autoComplete="off"
        className={`w-full pr-5 text-center ${INPUT_CLS} ${disabled ? "bg-gray-100 cursor-not-allowed text-gray-400" : inputColor}`}
      />
      <span
        onMouseDown={(e) => {
          e.preventDefault();
          if (!disabled) setOpen((o) => !o);
        }}
        className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer pointer-events-auto"
      >
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path
            d="M1 3l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </span>
      {open && !disabled && options.length > 0 && (
        <ul
          className="absolute z-50 left-0 right-0 mt-1 bg-white border border-blue-200 rounded-lg shadow-lg overflow-y-auto text-xs"
          style={{ maxHeight: "160px" }}
        >
          {options.map((i) => (
            <li
              key={i}
              onMouseDown={() => select(i)}
              className={`px-3 py-1.5 cursor-pointer hover:bg-blue-50 text-center ${
                i === value
                  ? "bg-blue-50 font-semibold text-blue-700"
                  : getDiscColor(i)
              }`}
            >
              {i}%
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const newEmptyRow = () => ({
  _isManual: true,
  stockId: `__new__${Date.now()}`,
  productName: "",
  medicineType: "",
  hsnCode: "",
  gstRate: 0,
  baseMrp: "",
  mrp: "",
  quantity: 0,
  salesDiscount: 0,
  buyerDiscount: 0,
  sellPrice: "0",
  gstAmount: "0",
  averagePurchasePrice: "",
  batchNo: "",
  stockIds: null,
  size: "",
  isManualEntry: true,
});

const isRowFilled = (row) =>
  row.productName.trim() !== "" &&
  row.baseMrp !== "" &&
  parseFloat(row.baseMrp) > 0;

// Recalculate mrp (baseMrp * qty), sellPrice (discounted + GST inclusive), gstAmount (per row)
const recalc = (row) => {
  const base = parseFloat(row.baseMrp) || 0;
  const qty = parseInt(row.quantity) || 0;
  const disc = parseFloat(row.salesDiscount) || 0;
  const gstRate = parseFloat(row.gstRate) || 0;
  const mrp = base * qty;
  const size= row.size ? parseFloat(row.size) : 0;
  const discounted = mrp - (mrp * disc) / 100;
  const gst = (discounted * gstRate) / 100;
  const sell = discounted + gst; // Sell price = discounted price + GST
  return {
    ...row,
    mrp: mrp > 0 ? mrp.toFixed(2) : "",
    sellPrice: mrp > 0 ? sell.toFixed(2) : "0",
    gstAmount: discounted > 0 ? gst.toFixed(2) : "0",
  };
};

const OrderCreate = () => {
  const [fieldConfig, setFieldConfig] = useState({});
  const { showLoader, hideLoader } = useLoader();
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchBy, setSearchBy] = useState("name");
  const [products, setProducts] = useState([]);
  const [searching, setSearching] = useState(false);
  const [totalAvailableQty, setTotalAvailableQty] = useState(null);
  const [cart, setCart] = useState([newEmptyRow()]);
  const [customerMobile, setCustomerMobile] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [paidAmount, setPaidAmount] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [barcodeInput, setBarcodeInput] = useState("");
  const [barcodeScanning, setBarcodeScanning] = useState(false);
  
  useEffect(() => {
    stockService
      .getClientFieldConfig()
      .then((res) => {
        const configMap = {};

        (res || []).forEach((f) => {
          configMap[f.fieldName] = f.visible;
        });

        const normalizeConfig = (config) => {
          const result = {};

          Object.keys(config || {}).forEach((key) => {
            const val = config[key];

            if (typeof val === "boolean") {
              result[key] = { visible: val, mandatory: false };
            } else {
              result[key] = {
                visible: !!val?.visible,
                mandatory: !!val?.mandatory,
              };
            }
          });

          return result;
        };

        setFieldConfig(normalizeConfig(configMap));
        // setFieldConfig(configMap);
      })
      .catch(() => setFieldConfig({}));
  }, []);
  const handlePaymentTypeChange = (val) => {
    setPaymentType(val);
    if (val === "CASH") setTransactionId("NA");
    else if (transactionId === "NA") setTransactionId("");
  };
  const [categories, setCategories] = useState([]);
  const [toast, setToast] = useState(null);
  const [orderSaved, setOrderSaved] = useState(false);
  const [successOrderId, setSuccessOrderId] = useState(null);
  const [successCustomerId, setSuccessCustomerId] = useState(null);
  const [showWhatsAppPopup, setShowWhatsAppPopup] = useState(false);
  const [whatsAppBillEnabled, setWhatsAppBillEnabled] = useState(false);

  useEffect(() => {
    stockService.getWhatsAppBillEnabled()
      .then((res) => setWhatsAppBillEnabled(res?.value === true))
      .catch(() => setWhatsAppBillEnabled(false));
  }, []);
  const {
    downloadBillPDF,
    generateBillByOrderId,
    loading: billLoading,
  } = useBill();
  const navigate = useNavigate();

  useEffect(() => {
    stockService
      .getAllCategories()
      .then((data) => setCategories(Array.isArray(data) ? data : []))
      .catch(() => setCategories([]));
  }, []);

  const filledCart = cart.filter((i) => !i._isManual || isRowFilled(i));
  const totalGst = filledCart.reduce(
    (t, i) => t + (parseFloat(i.gstAmount) || 0),
    0,
  );
  const finalBillRaw = filledCart.reduce(
    (t, i) => t + (parseFloat(i.sellPrice) || 0),
    0,
  );
  const finalBill = Math.round(finalBillRaw);
  const paidAmountNum = parseFloat(paidAmount) || 0;
  const dueAmount = finalBill - paidAmountNum;

  const handleSearch = async (query) => {
    setSearchTerm(query);
    if (query.length >= 3) {
      setSearching(true);
      try {
        const res = await stockService.checkStockAvailability(query, searchBy);
        setTotalAvailableQty(res?.totalAvailableQuantity ?? null);
        const list = Array.isArray(res) ? res : res.groupedStocks || [];
        setProducts(list.map((i) => ({ ...i, stockId: i.stockIds?.[0] })));
      } catch {
        setProducts([]);
        setTotalAvailableQty(null);
      } finally {
        setSearching(false);
      }
    } else {
      setProducts([]);
      setTotalAvailableQty(null);
    }
  };

  // Barcode scan handler - searches by barcode and auto-adds to cart
  const handleBarcodeScan = async (barcode) => {
    if (!barcode || barcode.trim().length < 3) return;
    setBarcodeScanning(true);
    try {
      const stock = await stockService.searchByBarcode(barcode.trim());
      if (!stock || !stock.productName) {
        setToast({ type: "error", message: "No product found for this barcode" });
        return;
      }
      const productStockId = stock.stockIds?.[0] ?? stock.stockId;
      setCart((prev) => {
        const existingIdx = prev.findIndex((i) => i.stockId === productStockId && !i._isManual);
        if (existingIdx >= 0) {
          const existing = prev[existingIdx];
          const newQty = (parseInt(existing.quantity) || 0) + 1;
          if (newQty > existing.maxQty) {
            setToast({ type: "error", message: `Max available qty is ${existing.maxQty}` });
            return prev;
          }
          const updated = [...prev];
          updated[existingIdx] = recalc({ ...existing, quantity: newQty });
          return updated;
        }
        const withoutTrailing = prev.filter((i) => !(i._isManual && !isRowFilled(i)));
        const base = parseFloat(stock.mrp) || 0;
        const newRow = recalc({
          _isManual: false,
          stockId: productStockId,
          productId: stock.productId ?? null,
          productName: stock.productName || "",
          medicineType: stock.medicineType || "",
          baseMrp: base,
          hsnCode: stock.hsn || stock.hsnCode || "",
          mrp: "",
          quantity: 1,
          maxQty: stock.totalQuantity || stock.quantity || 0,
          salesDiscount: 0,
          buyerDiscount: 0,
          averageSalesDiscount: parseFloat(stock.averageSalesDiscount) || 0,
          sellPrice: "0",
          size: stock.size ?? "",
          averagePurchasePrice: stock.averagePurchasePrice ?? "",
          stockIds: stock.stockIds ?? (stock.stockId ? [stock.stockId] : null),
          batchNumbers: stock.batchNumbers ?? (stock.batchNo ? [stock.batchNo] : null),
          isManualEntry: false,
        });
        return ensureTrailingEmptyRow([...withoutTrailing, newRow]);
      });
    } catch {
      setToast({ type: "error", message: "No product found for this barcode" });
    } finally {
      setBarcodeScanning(false);
      setBarcodeInput("");
    }
  };

  const ensureTrailingEmptyRow = (rows) => {
    const last = rows[rows.length - 1];
    if (!last || !last._isManual || isRowFilled(last)) {
      return [...rows, newEmptyRow()];
    }
    return rows;
  };

  const toggleCart = (product) => {
    setCart((prev) => {
      const exists = prev.some((i) => i.stockId === product.stockId);
      let updated;
      if (exists) {
        updated = prev.filter((i) => i.stockId !== product.stockId);
      } else {
        const withoutTrailing = prev.filter(
          (i) => !(i._isManual && !isRowFilled(i)),
        );
        const base = parseFloat(product.mrp) || 0;
        const newRow = recalc({
          _isManual: false,
          stockId: product.stockIds?.[0] ?? product.stockId,
          productId: product.productId ?? null,
          productName: product.productName || "",
          medicineType: product.medicineType || "",
          baseMrp: base,
          hsnCode: product.hsn || "",
          mrp: "",
          quantity: 1,
          maxQty: product.totalQuantity || 0,
          salesDiscount: 0,
          buyerDiscount: 0,
          averageSalesDiscount: parseFloat(product.averageSalesDiscount) || 0,
          sellPrice: "0",
          size: product.size ?? "",
          averagePurchasePrice: product.averagePurchasePrice ?? "",
          stockIds: product.stockIds ?? null,
          batchNumbers: product.batchNumbers ?? null,
          isManualEntry: false,
        });
        updated = [...withoutTrailing, newRow];
        setCatInput((p) => ({
          ...p,
          [product.stockId]: product.medicineType || "",
        }));
      }
      return ensureTrailingEmptyRow(updated);
    });
  };

  const updateQty = (stockId, change) =>
    setCart((prev) =>
      prev.map((i) => {
        if (i.stockId !== stockId) return i;

        let newQty = i.quantity + change;

        if (!i._isManual) {
          if (newQty > i.maxQty) {
            setToast({
              type: "error",
              message: `Max available quantity is ${i.maxQty}`,
            });
            newQty = i.maxQty;
          }
        }

        return recalc({
          ...i,
          quantity: Math.max(0, newQty),
        });
      }),
    );

  const setQty = (stockId, val) =>
    setCart((prev) =>
      prev.map((i) => {
        if (i.stockId !== stockId) return i;

        const inputQty = Math.max(0, parseInt(val) || 0);

        // ✅ apply max limit only for stock items
        const finalQty = !i._isManual
          ? Math.min(inputQty, i.maxQty || 0)
          : inputQty;

        if (!i._isManual && inputQty > i.maxQty) {
          setToast({
            type: "error",
            message: `Max available quantity is ${i.maxQty}`,
          });
        }

        return recalc({ ...i, quantity: finalQty });
      }),
    );

  const setItemDiscount = (stockId, val, rowIdx) => {
    const parsed = Math.min(100, Math.max(0, parseFloat(val) || 0));
    const row = cart.find((i) => i.stockId === stockId);
    if (!row) return;
    if (!row._isManual) {
      // stock row: cap by averageSalesDiscount
      const maxDisc = parseFloat(row.averageSalesDiscount) || 0;
      if (maxDisc > 0 && parsed > maxDisc) {
        setToast({
          type: "error",
          message: `Row ${rowIdx + 1}: Disc % cannot exceed ${maxDisc}%`,
        });
        return;
      }
    } else {
      // manual row: cap by buyerDiscount from category
      if (row.buyerDiscount > 0 && parsed > row.buyerDiscount) {
        setToast({
          type: "error",
          message: `Row ${rowIdx + 1}: Disc % cannot be greater than buyer discount (${row.buyerDiscount}%)`,
        });
        return;
      }
    }
    setCart((prev) =>
      prev.map((i) =>
        i.stockId === stockId ? recalc({ ...i, salesDiscount: parsed }) : i,
      ),
    );
  };

  const [catInput, setCatInput] = useState({});
  const [catOpen, setCatOpen] = useState({});

  const handleCategoryInput = (stockId, val) => {
    setCatInput((p) => ({ ...p, [stockId]: val }));
    setCatOpen((p) => ({ ...p, [stockId]: true }));
    if (!val) {
      setCart((prev) =>
        prev.map((i) =>
          i.stockId === stockId
            ? { ...i, medicineType: "", salesDiscount: 0 }
            : i,
        ),
      );
    }
  };

  const handleCategorySelect = async (stockId, code, label) => {
    setCatInput((p) => ({ ...p, [stockId]: label }));
    setCatOpen((p) => ({ ...p, [stockId]: false }));
    setCart((prev) =>
      prev.map((i) =>
        i.stockId === stockId ? { ...i, medicineType: code } : i,
      ),
    );
    try {
      const cat = await stockService.getCategoryByCode(code);
      const disc = cat?.discount ?? cat?.defaultDiscount ?? 0;
      const gstRate = cat?.gstRate ?? 0;
      setCart((prev) =>
        prev.map((i) =>
          i.stockId === stockId
            ? recalc({
                ...i,
                medicineType: code,
                buyerDiscount: disc,
                gstRate,
              })
            : i,
        ),
      );
      setCart((prev) =>
        prev.map((i) =>
          i.stockId === stockId
            ? recalc({ ...i, medicineType: code, buyerDiscount: disc, gstRate })
            : i,
        ),
      );
    } catch {}
  };

  const updateManualField = (stockId, field, value) => {
    setCart((prev) => {
      const updated = prev.map((i) => {
        if (i.stockId !== stockId) return i;
        const patched = { ...i, [field]: value };
        return recalc(patched);
      });
      return ensureTrailingEmptyRow(updated);
    });
  };

  const addRowAfter = (stockId) => {
    setCart((prev) => {
      const withoutTrailing = prev.filter(
        (i) => !(i._isManual && !isRowFilled(i)),
      );
      const idx = withoutTrailing.findIndex((i) => i.stockId === stockId);
      const result = [
        ...withoutTrailing.slice(0, idx + 1),
        newEmptyRow(),
        ...withoutTrailing.slice(idx + 1),
      ];
      return ensureTrailingEmptyRow(result);
    });
  };

  const remove = (stockId) =>
    setCart((prev) => {
      const updated = prev.filter((i) => i.stockId !== stockId);
      return ensureTrailingEmptyRow(updated.length === 0 ? [] : updated);
    });

  const isInCart = (stockId) => cart.some((i) => i.stockId === stockId);
  const isExpired = (d) => d && new Date(d) < new Date();
  const isExpiringSoon = (d) => {
    if (!d) return false;
    const diff = new Date(d) - new Date();
    return diff > 0 && diff < 90 * 24 * 60 * 60 * 1000;
  };

  const validateCart = () => {
    if (!customerMobile.trim() || customerMobile.trim().length !== 10) {
      setToast({
        type: "error",
        message: "Customer Mobile No is required (10 digits)",
      });
      return false;
    }
    if (!paymentType) {
      setToast({ type: "error", message: "Payment Type is required" });
      return false;
    }
    if (filledCart.length === 0) {
      setToast({ type: "error", message: "Add at least one product" });
      return false;
    }
    const FIELDS = [
      {
        key: "productName",
        label: "Product",
        check: (v) => !v || !String(v).trim(),
      },
      {
        key: "medicineType",
        label: "Category",
        check: (v) => !v || !String(v).trim(),
      },
      { key: "baseMrp", label: "MRP", check: (v) => !v || parseFloat(v) <= 0 },
      {
        key: "quantity",
        label: "Quantity",
        check: (v) => !v || parseInt(v) <= 0,
      },
      {
        key: "salesDiscount",
        label: "Disc %",
        check: (v) => v === "" || v === null || v === undefined,
      },
    ];
    for (let i = 0; i < filledCart.length; i++) {
      const row = filledCart[i];
      const missing = FIELDS.filter((f) => f.check(row[f.key])).map(
        (f) => f.label,
      );
      if (missing.length > 0) {
        setToast({
          type: "error",
          message: `Row ${i + 1}: ${missing.join(", ")} is required`,
        });
        return false;
      }
    }
    // Soft HSN warning (non-blocking)
    const missingHsn = filledCart
      .map((row, i) => (!row.hsnCode || !String(row.hsnCode).trim() ? i + 1 : null))
      .filter(Boolean);
    if (missingHsn.length > 0) {
      setToast({ type: "error", message: `HSN code missing for row(s): ${missingHsn.join(", ")} — continuing without HSN` });
    }
    return true;
  };

  const resetOrder = () => {
    setCart([newEmptyRow()]);
    setCustomerMobile("");
    setCustomerName("");
    setPaidAmount(0);
    setPaymentType("");
    setTransactionId("");
    setCatInput({});
    setCatOpen({});
    setOrderSaved(false);
    setSuccessOrderId(null);
    setSuccessCustomerId(null);

    setRefreshKey((prev) => prev + 1);
  };

  const [stockPopup, setStockPopup] = useState(null); // { status, items, message, reservationId, reservedStocks }

  const handleStockCancel = async () => {
    // Release reservation on cancel
    if (stockPopup?.reservationId && stockPopup?.reservedStocks?.length > 0) {
      try {
        await stockService.releaseStockReservation(
          null,
          stockPopup.reservationId,
          stockPopup.reservedStocks,
        );
      } catch {
        /* scheduler will auto-release */
      }
    }
    setStockPopup(null);
  };

  const handleStockContinue = async () => {
    const popup = stockPopup;
    // 1. Update cart: set partial items to available qty, remove zero-stock items
    let updatedCart = [...cart];
    popup.items.forEach((si) => {
      if (si.status === "OUT_OF_STOCK") {
        updatedCart = updatedCart.filter(
          (c) => !(c.productName === si.productName && !c._isManual),
        );
      } else if (si.status === "PARTIAL") {
        updatedCart = updatedCart.map((c) => {
          if (c.productName === si.productName && !c._isManual) {
            return recalc({ ...c, quantity: si.availableQty });
          }
          return c;
        });
      }
    });

    // 2. Remove items with 0 quantity
    updatedCart = updatedCart.filter((c) => c._isManual || c.quantity > 0);

    // 3. Check if any stock items remain
    const stockItems = updatedCart.filter((c) => !c._isManual);
    if (
      stockItems.length === 0 &&
      updatedCart.filter((c) => c._isManual && isRowFilled(c)).length === 0
    ) {
      setStockPopup(null);
      setToast({
        type: "error",
        message: "No items with available stock. Cannot create order.",
      });
      return;
    }

    setCart(ensureTrailingEmptyRow(updatedCart));
    setStockPopup(null);

    // 4. Auto-retry order with updated quantities (reservation already held)
    try {
      const filledItems = updatedCart.filter(
        (i) => !i._isManual || isRowFilled(i),
      );
      const res = await orderService.createOrder({
        customerMobile: customerMobile.trim(),
        customerName: customerName.trim() || null,
        paidAmount: parseFloat(paidAmount) || 0,
        finalAmount: filledItems.reduce(
          (t, i) => t + (parseFloat(i.sellPrice) || 0),
          0,
        ),
        paymentType,
        transactionId: transactionId.trim(),
        forceCreate: true, // Signal backend to skip validation (reservation already held)
        reservationId: popup.reservationId,
        item: filledItems.map((p) => ({
          name: p.productName || p.name,
          hsnCode: Array.isArray(p.hsnCode) ? (p.hsnCode[0] || null) : (p.hsnCode || null),
          gstRate: p.gstRate || 0,
          mrp: parseFloat(p.mrp) || 0,
          sellPrice: parseFloat(p.sellPrice) || 0,
          quantity: p.quantity,
          salesDiscount: p.salesDiscount,
          buyerDiscount: p.buyerDiscount || 0,
          size: p.size || 0,
          stockIds: p.stockIds ?? null,
          batchNumbers: p._isManual
            ? p.batchNo
              ? [p.batchNo]
              : null
            : (p.batchNumbers ?? null),
          isManualEntry: p.isManualEntry ?? false,
          averagePurchasePrice: p.averagePurchasePrice || null,
          stockId: p._isManual ? null : (p.stockId ?? null),
          productId: p._isManual ? null : (p.productId ?? null),
          supplyType: "INTRA",
        })),
      });

      if (res?.stockStatus && res.stockStatus !== "FULL_AVAILABLE") {
        // Edge case: stock changed again between popup and retry
        setStockPopup({
          status: res.stockStatus,
          items: res.stockItems || [],
          message: res.message,
          reservationId: res.reservationId || null,
          reservedStocks: res.reservedStocks || [],
        });
        return;
      }

      const orderId = res?.orderId ?? res?.id ?? null;
      setSuccessOrderId(orderId);
      setSuccessCustomerId(res?.customerId ?? null);
      setOrderSaved(true);
      setToast({
        type: "success",
        message: `Order created with available stock for ${customerMobile.trim()}`,
      });
    } catch (err) {
      const msg =
        err.response?.data?.errorMessage ||
        err.response?.data?.message ||
        "Failed to create order";
      setToast({ type: "error", message: msg });
    }
  };

  const [showPaidAlert, setShowPaidAlert] = useState(false);

  const placeOrder = async () => {
    if (!validateCart()) return;
    if (parseFloat(paidAmountNum.toFixed(2)) > parseFloat(finalBill.toFixed(2))) {
      setToast({ type: "error", message: "Paid amount cannot be greater than final bill" });
      return;
    }
    if (parseFloat(paidAmountNum.toFixed(2)) < parseFloat(finalBill.toFixed(2)) && !showPaidAlert) {
      setShowPaidAlert(true);
      return;
    }
    setShowPaidAlert(false);
    showLoader();
    try {
      const res = await orderService.createOrder({
        customerMobile: customerMobile.trim(),
        customerName: customerName.trim() || null,
        ...(orderSaved && successOrderId && { orderId: successOrderId }),
        ...(orderSaved &&
          successCustomerId && { customerId: successCustomerId }),
        paidAmount: parseFloat(paidAmount) || 0,
        finalAmount: finalBill,
        paymentType: paymentType,
        transactionId: transactionId.trim(),
        item: filledCart.map((p) => ({
          name: p.productName || p.name,
          hsnCode: Array.isArray(p.hsnCode) ? (p.hsnCode[0] || null) : (p.hsnCode || null),
          gstRate: p.gstRate || 0,
          mrp: parseFloat(p.mrp) || 0,
          sellPrice: parseFloat(p.sellPrice) || 0,
          quantity: p.quantity,
          salesDiscount: p.salesDiscount,
          buyerDiscount: p.buyerDiscount || 0,
          size: p.size || 0,
          stockIds: p.stockIds ?? null,
          batchNumbers: p._isManual
            ? p.batchNo
              ? [p.batchNo]
              : null
            : (p.batchNumbers ?? null),
          isManualEntry: p.isManualEntry ?? false,
          averagePurchasePrice: p.averagePurchasePrice || null,
          stockId: p._isManual ? null : (p.stockId ?? null),
          productId: p._isManual ? null : (p.productId ?? null),
          supplyType: "INTRA",
        })),
      });

      // Handle stock validation response (no order created yet)
      if (res?.stockStatus && res.stockStatus !== "FULL_AVAILABLE") {
        setStockPopup({
          status: res.stockStatus,
          items: res.stockItems || [],
          message: res.message,
          reservationId: res.reservationId || null,
          reservedStocks: res.reservedStocks || [],
        });
        return;
      }

      // Order created successfully
      const orderId = res?.orderId ?? res?.id ?? null;
      const customerId = res?.customerId ?? null;
      setSuccessOrderId(orderId);
      setSuccessCustomerId(customerId);

      setOrderSaved(true);
      setToast({
        type: "success",
        message: `Order created for ${customerMobile.trim()}`,
      });

      // ✅ OPEN WHATSAPP POPUP only if enabled for this client
      if (whatsAppBillEnabled) setShowWhatsAppPopup(true);
      // setOrderSaved(true);
      // setToast({
      //   type: "success",
      //   message: `Order created for ${customerMobile.trim()}`,
      // });
    } catch (err) {
      const msg =
        err.response?.data?.errorMessage ||
        err.response?.data?.message ||
        err.response?.data?.error ||
        (typeof err.response?.data === "string"
          ? err.response.data
          : "Server error.");
      setToast({ type: "error", message: msg });
    } finally {
      hideLoader(); // ✅ ALWAYS STOP LOADER
    }
  };

  const handleWhatsAppSend = async () => {
    try {
      await downloadBillPDF(successOrderId);

      const itemLines = filledCart.map((item, idx) =>
        `${idx + 1}. ${item.productName}${item.size ? " (Size: " + item.size + ")" : ""} x${item.quantity} | MRP: ${item.mrp} | Disc: ${item.salesDiscount}% | Total: ${item.sellPrice}`
      ).join("\n");

      const custLine = customerName ? `Customer: ${customerName}\n` : "";
            const companyName = localStorage.getItem("companyName") || "";
      const message = `${companyName ? `*${companyName}*\n` : ""}*Invoice*\n\nOrder ID: ${successOrderId}\n${custLine}Mobile: ${customerMobile}\n\n*Items:*\n${itemLines}\n\n*Summary:*\nTotal Items: ${filledCart.length}\nFinal Amount: ${finalBill.toFixed(2)}\nPaid: ${paidAmountNum.toFixed(2)}\nDue: ${(finalBill - paidAmountNum).toFixed(2)}\n\nThank you!`;

      const url = `https://wa.me/91${customerMobile}?text=${encodeURIComponent(message)}`;
      window.open(url, "_blank");
      setShowWhatsAppPopup(false);
      setToast({ type: "success", message: "WhatsApp opened. Attach PDF manually." });
    } catch (err) {
      setToast({ type: "error", message: "Failed to open WhatsApp" });
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden bg-gradient-to-tr from-blue-50 via-indigo-50 to-teal-50 p-3 sm:p-4 min-w-0">
      <Header showSearch={false} showCart={false} />
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}

      <div className="flex flex-col lg:flex-row gap-3 mt-3 flex-1 min-h-0">
        {/* LEFT: Medicine Search Panel */}
        <section className="w-full lg:w-56 flex-shrink-0 flex flex-col rounded-xl overflow-hidden shadow-md border border-blue-200 max-h-44 sm:max-h-56 lg:max-h-none">
          <div className="bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200 px-3 py-2 flex-shrink-0">
            <div className="flex items-center justify-between mb-1.5">
              <h2 className="text-xs font-bold text-blue-800">
                {" "}
                Product Search
              </h2>
              {totalAvailableQty !== null && (
                <span className="text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                  <i className="fas fa-cubes mr-1" />
                  {totalAvailableQty}
                </span>
              )}
            </div>
            {/* Barcode Scanner Input */}
            <div className="flex items-center gap-1.5 bg-yellow-50 border border-yellow-300 rounded-lg px-2 py-1 mb-1.5 focus-within:ring-2 focus-within:ring-yellow-400">
              <i className={`fas fa-barcode text-xs ${barcodeScanning ? "text-yellow-500" : "text-yellow-600"}`} />
              <input
                type="text"
                value={barcodeInput}
                onChange={(e) => setBarcodeInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleBarcodeScan(barcodeInput); }}
                placeholder="Scan barcode..."
                className="flex-1 text-xs text-gray-800 placeholder-gray-400 focus:outline-none bg-transparent"
                autoComplete="off"
                autoFocus
              />
              {barcodeScanning && <i className="fas fa-spinner fa-spin text-yellow-500 text-xs" />}
            </div>
            <div className="flex items-center gap-1.5 bg-white border border-blue-300 rounded-lg px-2 py-1 focus-within:ring-2 focus-within:ring-blue-400">
              <select
                value={searchBy}
                onChange={(e) => { setSearchBy(e.target.value); setSearchTerm(""); setProducts([]); }}
                className="text-xs bg-transparent border-none focus:outline-none text-blue-600 font-semibold cursor-pointer pr-0"
              >
                <option value="name">Name</option>
                <option value="batchNo">Batch</option>
              </select>
              <i
                className={`fas fa-search text-xs ${searching ? "text-blue-400" : "text-gray-400"}`}
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder={searchBy === "name" ? "Search by name..." : "Search by batch..."}
                className="flex-1 text-xs text-gray-800 placeholder-gray-400 focus:outline-none bg-transparent"
                autoComplete="off"
              />
              {searching && (
                <i className="fas fa-spinner fa-spin text-blue-400 text-xs" />
              )}
              {searchTerm && !searching && (
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setProducts([]);
                  }}
                  className="text-gray-300 hover:text-gray-500"
                >
                  <i className="fas fa-times text-xs" />
                </button>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto bg-white">
            {!searchTerm ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-300 py-12">
                <i className="fas fa-search text-4xl mb-3" />
                <p className="text-sm text-gray-400">Search products above</p>
                <p className="text-xs text-gray-300 mt-1">
                  Min 3 characters to search
                </p>
              </div>
            ) : searching ? (
              <div className="flex items-center justify-center h-full py-12 text-blue-400">
                <i className="fas fa-spinner fa-spin text-2xl" />
              </div>
            ) : products.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-300 py-12">
                <i className="fas fa-box-open text-4xl mb-3" />
                <p className="text-sm text-gray-400">No products found</p>
              </div>
            ) : (
              products.map((product) => {
                const inCart = isInCart(product.stockId);
                const expired = isExpired(product.expiryDate);
                const expiring = !expired && isExpiringSoon(product.expiryDate);
                return (
                  <label
                    key={product.stockId}
                    className={`flex items-center gap-2 px-3 py-2 border-b border-gray-50 cursor-pointer transition-colors ${
                      inCart
                        ? "bg-blue-50"
                        : expired
                          ? "bg-red-50 opacity-70"
                          : "hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={inCart}
                      disabled={expired}
                      onChange={() => !expired && toggleCart(product)}
                      className="w-3.5 h-3.5 accent-blue-500 flex-shrink-0 cursor-pointer"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 flex-wrap">
                        <span
                          className={`text-xs font-semibold truncate ${inCart ? "text-blue-800" : "text-gray-800"}`}
                        >
                          {product.productName}
                        </span>
                        {expired && (
                          <span className="text-xs bg-red-100 text-red-600 px-1 py-0.5 rounded-full">
                            Exp
                          </span>
                        )}
                        {expiring && (
                          <span className="text-xs bg-yellow-100 text-yellow-600 px-1 py-0.5 rounded-full">
                            Soon
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        {product.medicineType && (
                          <span className="text-xs text-gray-400 truncate">
                            {product.medicineType}
                          </span>
                        )}
                        <span
                          className={`text-xs font-medium ${product.totalQuantity <= 5 ? "text-red-500" : "text-green-600"}`}
                        >
                          Qty:{product.totalQuantity}
                        </span>
                        {fieldConfig?.size?.visible && (
                          <span className="text-xs font-bold text-red-700 flex-shrink-0">
                            size:{product.size}
                          </span>
                        )}
                        {fieldConfig?.boxNo?.visible && product.boxNumbers?.[0] && (
                          <span className="text-xs font-bold text-purple-700 flex-shrink-0">
                            box:{product.boxNumbers[0]}
                          </span>
                        )}
                      </div>
                    </div>

                    <span className="text-xs font-bold text-green-700 flex-shrink-0">
                      {product.mrp}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </section>

        <section className="flex-1 flex flex-col rounded-xl overflow-hidden shadow-md border border-blue-200 min-w-0 min-h-0">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200 px-3 sm:px-4 py-2 sm:py-2.5 flex flex-wrap items-center justify-between gap-2 flex-shrink-0">
            <h2 className="text-sm font-bold text-blue-800">
              {orderSaved ? "Update Invoice" : "Create Invoice"}
              {filledCart.length > 0 && (
                <span className="ml-1.5 text-xs font-normal text-blue-600">
                  ({filledCart.length})
                </span>
              )}
            </h2>
            <div className="flex items-center gap-2 flex-wrap">
              {orderSaved && (
                <button
                  onClick={() => window.location.reload()}
                  className="flex items-center gap-1 text-xs font-semibold text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg px-2.5 py-1 hover:bg-indigo-100 transition-colors"
                >
                  <i className="fas fa-plus text-xs" /> New Invoice
                </button>
              )}
              <div className="flex items-center gap-1 bg-white border border-blue-200 rounded-lg px-2 py-1 focus-within:ring-2 focus-within:ring-blue-400">
                <i className="fas fa-user text-gray-400 text-xs" />
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Customer Name"
                  readOnly={orderSaved}
                  className="w-28 text-xs text-gray-700 placeholder-gray-400 focus:outline-none bg-transparent"
                />
              </div>
              <div
                className={`flex items-center gap-1 bg-white border rounded-lg px-2 py-1 focus-within:ring-2 focus-within:ring-blue-400 ${
                  !customerMobile.trim() ? "border-red-300" : "border-blue-200"
                }`}
              >
                <i className="fas fa-mobile-alt text-gray-400 text-xs" />
                <input
                  type="tel"
                  value={customerMobile}
                  onChange={(e) =>
                    setCustomerMobile(
                      e.target.value.replace(/\D/g, "").slice(0, 10),
                    )
                  }
                  placeholder="Cust Mob No *"
                  maxLength="10"
                  readOnly={orderSaved}
                  className="w-28 text-xs text-gray-700 placeholder-gray-400 focus:outline-none bg-transparent"
                />
              </div>
              {!orderSaved && filledCart.length > 0 && (
                <button
                  onClick={() => setCart([newEmptyRow()])}
                  className="text-xs text-red-400 hover:text-red-600 transition-colors"
                >
                  <i className="fas fa-trash-alt mr-1" />
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto bg-white">
            <table className="min-w-[700px] w-full text-sm">
              <colgroup>
                <col style={{ width: "4%" }} /> {/* # */}
                <col style={{ width: "18%" }} /> {/* Product */}
                <col style={{ width: "12%" }} /> {/* Category */}
                <col style={{ width: "10%" }} /> {/* HSN */}
                <col style={{ width: "10%" }} /> {/* Batch No */}
                <col style={{ width: "11%" }} /> {/* MRP */}
                <col style={{ width: "10%" }} /> {/* Quantity */}
                <col style={{ width: "8%" }} /> {/* Disc % */}
                <col style={{ width: "10%" }} /> {/* Sell Price */}
                <col style={{ width: "9%" }} /> {/* GST */}
                <col style={{ width: "5%" }} /> {/* Actions */}
              </colgroup>
              <thead className="bg-gradient-to-r from-blue-100 via-indigo-50 to-teal-50 text-blue-900 text-xs font-semibold uppercase tracking-wide sticky top-0 z-10">
                <tr>
                  <th className="py-2.5 px-3 text-center border-b border-blue-200 w-[40px]">
                    #
                  </th>

                  <th className="py-2.5 px-4 text-left border-b border-blue-200 min-w-[140px]">
                    Category
                  </th>

                  <th className="py-2.5 px-4 text-left border-b border-blue-200 min-w-[220px]">
                    Product
                  </th>

                  {fieldConfig?.size?.visible && (
                    <th className="py-2.5 px-4 text-center border-b border-blue-200 w-[80px]">
                      Size
                    </th>
                  )}


                  <th className="py-2.5 px-4 text-center border-b border-blue-200 w-[80px]">
                    Qty
                  </th>

                  <th className="py-2.5 px-4 text-left border-b border-blue-200 min-w-[100px]">
                    HSN
                  </th>

                  <th className="py-2.5 px-4 text-left border-b border-blue-200 min-w-[120px]">
                    Batch
                  </th>

                  <th className="py-2.5 px-4 text-left border-b border-blue-200 w-[90px]">
                    Disc %
                  </th>

                  <th className="py-2.5 px-4 text-left border-b border-blue-100 w-[90px]">
                    GST %
                  </th>

                  <th className="py-2.5 px-4 text-left border-b border-blue-100 min-w-[120px]">
                    MRP
                  </th>

                  <th className="py-2.5 px-4 text-left border-b border-blue-200 min-w-[140px]">
                    Sell
                  </th>

                  <th className="py-2.5 px-3 text-center border-b border-blue-200 w-[40px]"></th>
                </tr>
              </thead>
              <tbody>
                {cart.map((item, idx) => {
                  const isManual = item._isManual;
                  const filled = isRowFilled(item);
                  const isLast = idx === cart.length - 1;
                  const showQtyControls = item.quantity > 0;

                  return (
                    <tr
                      key={item.stockId}
                      className={`border-b border-gray-100 transition-colors ${idx % 2 === 0 ? "bg-white" : "bg-slate-50"} hover:bg-blue-50`}
                    >
                      {/* # */}
                      <td className="py-2 px-2 text-center text-gray-400 text-xs font-medium">
                        {isManual && !filled ? "" : idx + 1}
                      </td>

                      {/* Category combobox */}
                      <td className="py-2 px-2">
                        <div className="relative w-full">
                          <input
                            type="text"
                            value={
                              catInput[item.stockId] ??
                              (item.medicineType || "")
                            }
                            readOnly={!item._isManual}
                            onChange={(e) =>
                              handleCategoryInput(item.stockId, e.target.value)
                            }
                            onFocus={() =>
                              setCatOpen((p) => ({
                                ...p,
                                [item.stockId]: true,
                              }))
                            }
                            onBlur={() =>
                              setTimeout(
                                () =>
                                  setCatOpen((p) => ({
                                    ...p,
                                    [item.stockId]: false,
                                  })),
                                150,
                              )
                            }
                            placeholder="Category"
                            autoComplete="off"
                            className={`w-full pr-6 ${INPUT_CLS}`}
                          />
                          <span
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setCatOpen((p) => ({
                                ...p,
                                [item.stockId]: !p[item.stockId],
                              }));
                            }}
                            className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer pointer-events-auto"
                          >
                            <svg
                              width="10"
                              height="10"
                              viewBox="0 0 10 10"
                              fill="none"
                            >
                              <path
                                d="M1 3l4 4 4-4"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                              />
                            </svg>
                          </span>
                          {catOpen[item.stockId] &&
                            (() => {
                              const q = (
                                catInput[item.stockId] || ""
                              ).toLowerCase();
                              const opts = categories.filter((c) =>
                                (c.type || "").toLowerCase().includes(q),
                              );
                              return opts.length > 0 ? (
                                <ul className="absolute z-50 left-0 right-0 mt-1 bg-white border border-blue-200 rounded-lg shadow-lg max-h-40 overflow-y-auto text-xs">
                                  {opts.map((c) => (
                                    <li
                                      key={c.code}
                                      onMouseDown={() =>
                                        handleCategorySelect(
                                          item.stockId,
                                          c.code,
                                          c.type,
                                        )
                                      }
                                      className="px-3 py-1.5 cursor-pointer hover:bg-blue-50 text-gray-700"
                                    >
                                      {c.type}
                                    </li>
                                  ))}
                                </ul>
                              ) : null;
                            })()}
                        </div>
                      </td>

                      {/* Product */}
                      <td className="py-2 px-2">
                        <input
                          type="text"
                          value={item.productName}
                          readOnly={!isManual}
                          onChange={(e) =>
                            isManual &&
                            updateManualField(
                              item.stockId,
                              "productName",
                              e.target.value,
                            )
                          }
                          placeholder="Product name"
                          className={`w-full ${INPUT_CLS} ${!isManual ? "cursor-default" : ""}`}
                        />
                      </td>
                      {/* Size Column */}
                      {fieldConfig?.size?.visible && (
                        <td className="py-2 px-2">
                          <div className="flex items-center justify-center gap-1">
                            <input
                              type="number"
                              value={item.size || ""}
                              readOnly={!isManual}
                              onChange={(e) =>
                                isManual &&
                                updateManualField(
                                  item.stockId,
                                  "size",
                                  e.target.value,
                                )
                              }
                              placeholder="0"
                              className="w-12 text-center font-semibold text-blue-800 border border-blue-200 rounded-lg py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white placeholder-gray-400"
                            />
                          </div>
                        </td>
                      )}
                      {/* Quantity */}
                      <td className="py-2 px-2">
                        <div className="flex items-center justify-center gap-1">
                          <input
                            type="number"
                            value={item.quantity || ""}
                            min="0"
                            max={!item._isManual ? item.maxQty : undefined}
                            placeholder="0"
                            onChange={(e) =>
                              setQty(item.stockId, e.target.value)
                            }
                            className="w-12 text-center font-semibold text-blue-800 border border-blue-200 rounded-lg py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white placeholder-gray-400"
                          />
                        </div>
                      </td>
                      <td className="py-2 px-2">
                        <input
                          type="text"
                          value={item.hsnCode || ""}
                          onChange={(e) =>
                            isManual &&
                            updateManualField(
                              item.stockId,
                              "hsnCode",
                              e.target.value,
                            )
                          }
                          placeholder="HSN"
                          className={`w-full ${INPUT_CLS} ${!isManual ? "cursor-default" : ""}`}
                          readOnly={!isManual}
                        />
                      </td>
                      {/* Batch No */}
                      <td className="py-2 px-2">
                        {isManual ? (
                          <input
                            type="text"
                            value={item.batchNo || ""}
                            onChange={(e) =>
                              updateManualField(
                                item.stockId,
                                "batchNo",
                                e.target.value,
                              )
                            }
                            placeholder="Batch no"
                            className={`w-full ${INPUT_CLS}`}
                          />
                        ) : (
                          <input
                            type="text"
                            readOnly
                            value={item.batchNumbers?.join(", ") || "-"}
                            title={item.batchNumbers?.join(", ") || ""}
                            className={`w-full ${INPUT_CLS} cursor-default text-gray-500`}
                          />
                        )}
                      </td>

                      {/* Disc % */}
                      <td className="py-2 px-2">
                        <DiscountCombobox
                          value={item.salesDiscount}
                          maxDiscount={
                            !isManual
                              ? parseFloat(item.averageSalesDiscount) || 0
                              : item.buyerDiscount > 0
                                ? item.buyerDiscount
                                : 100
                          }
                          disabled={isManual && !item.medicineType}
                          onChange={(val) =>
                            setItemDiscount(item.stockId, val, idx)
                          }
                          onError={(max) =>
                            setToast({
                              type: "error",
                              message: `Row ${idx + 1}: Disc % cannot exceed ${max}%`,
                            })
                          }
                          className="w-12 text-center font-semibold text-blue-800 border border-blue-200 rounded-lg py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white placeholder-gray-400"
                        />
                      </td>

                      {/* GST % */}
                      <td className="py-2 px-2 ">
                        <input
                          type="number"
                          value={
                            item.gstRate && item.gstRate !== 0
                              ? item.gstRate
                              : ""
                          }
                          placeholder="0"
                          min="0"
                          step="0.01"
                          onChange={(e) => {
                            const gstRate = parseFloat(e.target.value) || 0;
                            setCart((prev) =>
                              prev.map((i) => {
                                if (i.stockId !== item.stockId) return i;
                                return recalc({ ...i, gstRate });
                              }),
                            );
                          }}
                          className="w-12 text-center font-semibold text-blue-800 border border-blue-200 rounded-lg py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white placeholder-gray-400"
                        />
                      </td>

                      {/* MRP */}
                      <td className="py-2 px-2">
                        {isManual ? (
                          <input
                            type="text"
                            value={item._mrpFocused ? (item.baseMrp || "") : (item.mrp && parseFloat(item.mrp) > 0 ? item.mrp : (item.baseMrp || ""))}
                            onFocus={() => setCart((prev) => prev.map((i) => i.stockId === item.stockId ? { ...i, _mrpFocused: true } : i))}
                            onBlur={() => setCart((prev) => prev.map((i) => i.stockId === item.stockId ? { ...i, _mrpFocused: false } : i))}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (/^\d*\.?\d*$/.test(value)) {
                                updateManualField(item.stockId, "baseMrp", value);
                              }
                            }}
                            placeholder="Unit price"
                            className={`w-full text-right ${INPUT_CLS}`}
                          />
                        ) : (
                          <input
                            type="text"
                            readOnly
                            value={item.mrp || "0.00"}
                            className={`w-full text-right ${INPUT_CLS} cursor-default`}
                          />
                        )}
                      </td>

                      {/* Sell Price readonly */}
                      <td className="py-2 px-2">
                        <input
                          type="text"
                          readOnly
                          value={item.mrp !== "" ? `${item.sellPrice}` : "0.00"}
                          className={`w-full text-right ${DRP_CLS} cursor-default font-semibold text-green-1200`}
                        />
                      </td>

                      {/* Hidden averagePurchasePrice */}
                      <input
                        type="hidden"
                        value={item.averagePurchasePrice ?? ""}
                      />

                      {/* Actions */}
                      <td className="py-2 px-2">
                        <div className="flex items-center justify-center gap-1">
                          {isLast && filled && (
                            <button
                              onClick={() => addRowAfter(item.stockId)}
                              title="Add new row"
                              className="w-6 h-6 bg-green-50 hover:bg-green-100 rounded text-green-500 hover:text-green-700 flex items-center justify-center transition-colors"
                            >
                              <i className="fas fa-plus text-xs" />
                            </button>
                          )}
                          {(!isManual || filled || !isLast) && (
                            <button
                              onClick={() => remove(item.stockId)}
                              className="w-6 h-6 bg-red-50 hover:bg-red-100 rounded text-red-400 hover:text-red-600 flex items-center justify-center transition-colors"
                            >
                              <i className="fas fa-times text-xs" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Bill summary */}
          <div className="border-t border-blue-100 px-3 sm:px-5 py-2 sm:py-3 bg-gradient-to-r from-blue-50 to-indigo-50 flex-shrink-0">
            <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500">Paid Rs.</span>
                <input
                  type="number"
                  value={paidAmount}
                  min="0"
                  onChange={(e) => setPaidAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-20 text-center text-xs border border-blue-200 rounded-lg px-1 py-1 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                />
              </div>

              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500">Payment</span>
                <select
                  value={paymentType}
                  onChange={(e) => handlePaymentTypeChange(e.target.value)}
                  className="text-xs border border-blue-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                >
                  <option value="">Select</option>
                  <option value="CASH">Cash</option>
                  <option value="UPI">UPI</option>
                  <option value="CARD">Card</option>
                  <option value="BANK_TRANSFER">Bank Transfer</option>
                  <option value="CHEQUE">Cheque</option>
                </select>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500">Txn ID</span>
                <input
                  type="text"
                  value={transactionId}
                  onChange={(e) => setTransactionId(e.target.value)}
                  placeholder="Transaction ID *"
                  readOnly={paymentType === "CASH"}
                  className={`w-28 sm:w-36 text-xs border rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                    paymentType === "CASH"
                      ? "bg-gray-100 border-gray-200 cursor-not-allowed text-gray-500"
                      : "bg-white border-blue-200"
                  }`}
                />
              </div>
              <div className="ml-auto flex items-center gap-3">
                {(finalBill - finalBillRaw) !== 0 && (
                  <span className="text-xs text-gray-500">
                    Round off: <span className={(finalBill - finalBillRaw) > 0 ? "text-green-600" : "text-red-500"}>{(finalBill - finalBillRaw) > 0 ? "+" : ""}{(finalBill - finalBillRaw).toFixed(2)}</span>
                  </span>
                )}
                <span className="text-xs sm:text-sm font-bold text-gray-700">
                  Final Bill
                </span>
                <span className="text-sm sm:text-base font-bold text-blue-700">
                  ₹{finalBill.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Bottom Action Bar*/}
      <div className="flex-shrink-0 mt-2 bg-white rounded-xl shadow-md border border-blue-200 px-3 sm:px-4 py-2 flex flex-wrap items-center justify-between gap-2 sm:gap-3">
        <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
          {[
            //{ label: "Save Draft",       icon: "fa-save",         cls: "bg-gray-100 hover:bg-gray-200 text-gray-700",       onClick: null,                                                                                                                                                                                                                                                                    disabled: true },
            //  { label: "Generate Invoice", icon: "fa-file-invoice", cls: "bg-purple-50 hover:bg-purple-100 text-purple-700",  onClick: async () => { try { await generateBillByOrderId(successOrderId); navigate("/bill", { state: { orderId: successOrderId } }); } catch { setToast({ type: "error", message: "Failed to generate invoice" }); } }, disabled: !orderSaved || !successOrderId || billLoading },
            {
              label: "Download Bill",
              icon: "fa-print",
              cls: "bg-teal-50 hover:bg-teal-100 text-teal-700",
              onClick: async () => {
                try {
                  await downloadBillPDF(successOrderId);
                } catch {
                  setToast({ type: "error", message: "Failed to download bill" });
                }
              },
              disabled: !orderSaved || !successOrderId || billLoading,
            },
            {
              label: "Print Receipt",
              icon: "fa-receipt",
              cls: "bg-orange-50 hover:bg-orange-100 text-orange-700",
              onClick: () => {
                const pw = window.open("", "_blank");
                const items = filledCart.map((item, idx) =>
                  `<tr><td>${idx+1}</td><td>${item.productName}</td><td>${item.quantity}</td><td>${item.mrp}</td><td>${item.salesDiscount}%</td><td>${item.sellPrice}</td></tr>`
                ).join("");
                pw.document.write(`<!DOCTYPE html><html><head><title>Receipt</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: monospace; width: 80mm; margin: 0 auto; padding: 2mm; font-size: 10px; }
  .header { text-align: center; border-bottom: 1px dashed #000; padding-bottom: 3mm; margin-bottom: 2mm; }
  .header h2 { font-size: 13px; margin-bottom: 2px; }
  .header p { font-size: 9px; }
  .info { margin-bottom: 2mm; font-size: 9px; }
  .info div { display: flex; justify-content: space-between; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 2mm; }
  th, td { text-align: left; padding: 1px 2px; font-size: 9px; }
  th { border-bottom: 1px solid #000; }
  td:nth-child(3), td:nth-child(4), td:nth-child(5), td:nth-child(6),
  th:nth-child(3), th:nth-child(4), th:nth-child(5), th:nth-child(6) { text-align: right; }
  .summary { border-top: 1px dashed #000; padding-top: 2mm; font-size: 10px; }
  .summary div { display: flex; justify-content: space-between; padding: 1px 0; }
  .summary .total { font-weight: bold; font-size: 12px; border-top: 1px solid #000; margin-top: 2px; padding-top: 2px; }
  .footer { text-align: center; margin-top: 3mm; border-top: 1px dashed #000; padding-top: 2mm; font-size: 9px; }
  @media print { @page { margin: 0; size: 80mm auto; } body { width: 80mm; } }
</style></head><body>
<div class="header">
  <h2>${localStorage.getItem("companyName") || "Store"}</h2>
  <p>Receipt</p>
  <p>${localStorage.getItem("gstin") || ""}</p>
</div>
<div class="info">
  <div><span>Order: ${successOrderId || "-"}</span><span>${new Date().toLocaleDateString()}</span></div>
  <div><span>${customerName || ""}</span><span>${customerMobile}</span></div>
</div>
<table><thead><tr><th>#</th><th>Item</th><th>Qty</th><th>MRP</th><th>Disc</th><th>Total</th></tr></thead><tbody>${items}</tbody></table>
<div class="summary">
  <div><span>Items:</span><span>${filledCart.length}</span></div>
  <div class="total"><span>Final:</span><span>${finalBill.toFixed(2)}</span></div>
  <div><span>Paid:</span><span>${paidAmountNum.toFixed(2)}</span></div>
  <div><span>Due:</span><span>${(finalBill - paidAmountNum).toFixed(2)}</span></div>
</div>
<div class="footer">Thank you! Visit again.</div>
</body></html>`);
                pw.document.close();
                setTimeout(() => { pw.print(); setTimeout(() => pw.close(), 1000); }, 300);
              },
              disabled: !orderSaved || !successOrderId,
            },
          ].map(({ label, icon, cls, onClick, disabled }) => (
            <button
              key={label}
              onClick={onClick}
              disabled={disabled}
              className={`flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs font-semibold transition-colors ${
                disabled ? "bg-gray-100 text-gray-300 cursor-not-allowed" : cls
              }`}
            >
              <i className={`fas ${icon}`} /> {label}
            </button>
          ))}
        </div>
        {orderSaved ? (
          <button
            disabled
            className="flex items-center gap-2 px-4 sm:px-8 py-1.5 sm:py-2 rounded-full text-white font-bold text-xs sm:text-sm shadow-none bg-gray-300 cursor-not-allowed"
          >
            <i className="fas fa-check-circle" /> Order Created
          </button>
        ) : (
          <button
            onClick={placeOrder}
            disabled={filledCart.length === 0}
            className={`flex items-center gap-2 px-4 sm:px-8 py-1.5 sm:py-2 rounded-full text-white font-bold text-xs sm:text-sm shadow transition-all ${
              filledCart.length === 0
                ? "bg-gray-300 cursor-not-allowed shadow-none"
                : "bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 shadow-md"
            }`}
          >
            <i className="fas fa-shopping-bag" /> Generate Invoice
          </button>
        )}
      </div>

      {/* ✅ WhatsApp Popup (ADD HERE) */}
      {showWhatsAppPopup && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-5 w-[350px] shadow-xl">
            <h2 className="text-lg font-semibold mb-2">Send WhatsApp</h2>

            <p className="text-xs text-gray-500 mb-4">
              PDF will download automatically. Attach it in WhatsApp.
            </p>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowWhatsAppPopup(false)}
                className="px-3 py-1 border rounded-lg"
              >
                Cancel
              </button>

              <button
                onClick={handleWhatsAppSend}
                className="px-4 py-1 bg-green-600 text-white rounded-lg"
              >
                Send WhatsApp + PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Paid Amount Alert Popup */}
      {showPaidAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <i className="fas fa-exclamation-triangle text-amber-600" />
              </div>
              <h3 className="font-bold text-gray-800">Partial Payment</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Paid amount ({paidAmountNum.toFixed(2)}) is less than final bill ({finalBill.toFixed(2)}).
              Due amount will be {(finalBill - paidAmountNum).toFixed(2)}. Continue?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowPaidAlert(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 text-sm"
              >Cancel</button>
              <button
                onClick={() => placeOrder()}
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg text-sm font-semibold hover:from-blue-600 hover:to-indigo-700"
              >OK, Proceed</button>
            </div>
          </div>
        </div>
      )}

      {/* Stock Availability Popup */}
      {stockPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div
              className={`px-6 py-4 ${stockPopup.status === "OUT_OF_STOCK" ? "bg-gradient-to-r from-red-600 to-rose-600" : "bg-gradient-to-r from-amber-500 to-orange-500"}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <i
                    className={`fas ${stockPopup.status === "OUT_OF_STOCK" ? "fa-exclamation-triangle" : "fa-info-circle"} text-white text-lg`}
                  />
                </div>
                <div>
                  <h3 className="text-white font-bold">
                    {stockPopup.status === "OUT_OF_STOCK"
                      ? "Out of Stock"
                      : "Limited Stock"}
                  </h3>
                  <p className="text-white/80 text-xs">{stockPopup.message}</p>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 max-h-60 overflow-y-auto">
              {stockPopup.items?.map((item, idx) => (
                <div
                  key={idx}
                  className={`flex items-center justify-between py-2 ${idx > 0 ? "border-t border-gray-100" : ""}`}
                >
                  <span className="text-sm font-medium text-gray-800">
                    {item.productName}
                  </span>
                  <div className="text-right">
                    <span
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        item.status === "OUT_OF_STOCK"
                          ? "bg-red-100 text-red-700"
                          : item.status === "PARTIAL"
                            ? "bg-amber-100 text-amber-700"
                            : "bg-green-100 text-green-700"
                      }`}
                    >
                      {item.status === "OUT_OF_STOCK"
                        ? "No Stock"
                        : item.status === "PARTIAL"
                          ? `Only ${item.availableQty} available`
                          : "Available"}
                    </span>
                    {item.status === "PARTIAL" && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        Requested: {item.requestedQty}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
              {stockPopup.status === "OUT_OF_STOCK" ? (
                <button
                  onClick={handleStockCancel}
                  className="px-5 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm font-medium transition"
                >
                  OK
                </button>
              ) : (
                <>
                  <button
                    onClick={handleStockCancel}
                    className="px-5 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-100 text-sm font-medium transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleStockContinue}
                    className="px-5 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg text-sm font-semibold shadow transition"
                  >
                    Continue with Available
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderCreate;
